import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Card, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { ErrorComponent, LoadingSpinner } from '../../../../components';
import Product from './Product';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { GQLMerchantChannelProduct, GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { MerchantNavigation } from '../../navigation/MerchantNavigation';
import { Exception } from '@jewelutions/core-common-domain';
import { Terminal, Tool } from 'react-feather';

interface IProps {
  party: GQLParty;
}

const MerchantChannelCollectionProductList: React.FC<IProps> = ({ party }) => {
  const [errorPiecesComponentState, setErrorPiecesComponentState] = useState(false);
  const [errorCatalogComponentState, setErrorCatalogComponentState] = useState(false);
  const { t: translate } = useTranslation();
  const history = useHistory();

  const [state, setState] = useState({
    showNewProductDialog: false,
    selectedProduct: null,
    showVariants: false
  } as any);

  const {
    merchantChannelCatalogId,
    collectionId
  } = MerchantNavigation.merchantChannelCatalogCollectionProductList.paramsFromPath(useRouteMatch());

  const {
    loading: piecesLoading,
    error: piecesError,
    networkStatus: piecesNetworkStatus,
    data,
    refetch: refetchPieces
  } = GQLOperationBuilder.piece.getPiecesByMerchant({ merchantPartyId: party.id });

  const {
    loading: catalogLoading,
    error: catalogError,
    networkStatus: catalogNetworkStatus,
    data: catalogData,
    refetch: refetchCatalog
  } = GQLOperationBuilder.merchantChannelCatalog.getMerchantChannelCatalog({
    merchantChannelCatalogId: merchantChannelCatalogId
  });

  if (piecesLoading) return <LoadingSpinner />;

  if (piecesError && !errorPiecesComponentState)
    return (
      <ErrorComponent
        close={setErrorPiecesComponentState}
        details={piecesError?.stack}
        callback={refetchPieces}
        message={piecesError.message}
      />
    );

  if (catalogLoading) return <LoadingSpinner />;

  if (catalogError && !errorCatalogComponentState)
    return (
      <ErrorComponent
        close={setErrorCatalogComponentState}
        callback={refetchCatalog}
        message={catalogError?.message}
        details={catalogError?.stack}
      />
    );

  const mapProducts = (productsResponse: any) => {
    const catalog = GQLOperationBuilder.merchantChannelCatalog.merchantChannelCatalogFromResponse(
      productsResponse
    );
    const currentCollection = catalog.availableCollections.find(c => c.id === collectionId);
    if (currentCollection === undefined || currentCollection === null)
      throw new Exception(`Merchant channel catalog does not contain collection ${collectionId}`);
    const mappedProducts = currentCollection.products.map(prod => {
      const piece = GQLOperationBuilder.piece
        .pieceAssetArrayFromResponse(data)
        .find(itm => itm.id === prod.pieceVariants[0].pieceReference.pieceId);
      let icon = '';
      if (piece) {
        const svgs = piece.piece.models.map((itm: any) => itm.icon.url);
        if (svgs && svgs.length > 0) {
          icon = svgs[0];
        }
      }
      const pieceConfigurations = prod.pieceVariants.map(pv => {
        return pv.pieceReference.pieceConfiguration;
      });
      const materialTypes = Array.from(new Set(pieceConfigurations.map(mt => mt.materialType)));
      const platingTypes = Array.from(new Set(pieceConfigurations.map(mt => mt.materialType)));
      const stonePlacementTypes = Array.from(
        new Set(pieceConfigurations.map((mt: any) => mt.stonesPlacementType))
      );
      return {
        ...prod,
        icon,
        materialTypes,
        stonePlacementTypes,
        platingTypes
      };
    });
    return mappedProducts;
  };

  const onProductSelected = (product: GQLMerchantChannelProduct) => {
    setState({
      selectedProduct: product,
      showVariants: true
    });
  };

  const products = mapProducts(catalogData);

  const navigateToSettings = () => {
    history.push(
      MerchantNavigation.merchantChannelCatalogCollectionSettings.pathFromParams({
        merchantChannelCatalogId: merchantChannelCatalogId,
        collectionId: collectionId
      })
    );
  };

  const navigateToPreview = () => {
    history.push(
      MerchantNavigation.merchantChannelCatalogPreview.pathFromParams({
        merchantChannelCatalogId: merchantChannelCatalogId
      })
    );
  };

  return (
    <Fragment>
      <Grid container direction="row">
        <Grid item md={4} style={{ marginBottom: '8px' }}>
          <Card style={{ padding: '8px' }}>
            <Button
              variant="contained"
              color="primary"
              style={{ width: '50%' }}
              onClick={() => {
                navigateToSettings();
              }}
            >
              <Tool />
              <span style={{ marginLeft: '4px' }}>Collection Settings</span>
            </Button>
          </Card>
        </Grid>
        <Grid item md={4} style={{ marginBottom: '8px' }}>
          <Card style={{ padding: '8px' }}>
            <Button
              variant="contained"
              color="primary"
              style={{ width: '50%' }}
              onClick={() => {
                navigateToPreview();
              }}
            >
              <Terminal />
              <span style={{ marginLeft: '4px' }}>Web designer preview</span>
            </Button>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        {products.map(product => (
          <Product key={product.productIdentity} product={product} />
        ))}
      </Grid>
    </Fragment>
  );
};

export default MerchantChannelCollectionProductList;
